import { Routes } from '@angular/router';
import { MainPageComponent } from './pages/main-page/main-page.component';
import { SignUpPageComponent } from './pages/sign-up-page/sign-up-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { AuthenticatorComponent } from './pages/authenticator-page/authenticator/authenticator.component';
import { ForgotPasswordPageComponent } from './pages/forgot-password-page/forgot-password-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { ResetPassCongratulationsComponent } from './pages/reset-pass-pages/reset-pass-congratulations/reset-pass-congratulations.component';
import { ResetPassNewComponent } from './pages/password-reset-new-pass-page/reset-pass-new.component';
import { HomeBodyComponent } from './pages/home-page/home-body/home-body.component';
import { UserProfileSecurityComponent } from './pages/home-page/user-profile/user-profile-security/user-profile-security.component';
import { HeaderTabsComponent } from './pages/home-page/header-tabs/header-tabs.component';
import { SettingsGeneralComponent } from './pages/home-page/shop-settings/settings-general/settings-general.component';
import { UserProfileGeneralComponent } from './pages/home-page/user-profile/user-profile-general/user-profile-general.component';
import { SettingsRolesComponent } from './pages/home-page/shop-settings/settings-roles/settings-roles.component';
import { SettingsCategoriesComponent } from './pages/home-page/shop-settings/settings-categories/settings-categories.component';
import { InventoryComponent } from './pages/inventory/inventory.component';
import { InventoryTableComponent } from './pages/inventory/components/inventory-table/inventory-table.component';
import { CustomersComponent } from './pages/customers/customers.component';
import { CustomersTableComponent } from './components/shared/customers-table/customers-table.component';
import { UploadInventoryItemsComponent } from './pages/inventory/components/upload-inventory-items/upload-inventory-items.component';
import { authGuard } from './services/guards/auth.guard';
import { guestGuard } from './services/guards/guest.guard';
import { SettingsSubscriptionComponent } from './pages/home-page/shop-settings/settings-subscription/settings-subscription.component';
import { OrdersComponent } from './pages/orders/orders.component';
import { OrdersTableComponent } from './pages/orders/components/orders-table/orders-table.component';
import { StaffTableComponent } from './pages/staff/components/staff-table/staff-table.component';
import { ActiveSessionsComponent } from './pages/home-page/user-profile/active-sessions/active-sessions.component';
import { InactiveLinkPageComponent } from './pages/inactive-link-page/inactive-link-page.component';
import { UserProfileNotificationsComponent } from './pages/home-page/user-profile/user-profile-notifications/user-profile-notifications.component';
import { UserProfileDataAndPrivacyComponent } from './pages/home-page/user-profile/user-profile-data-and-privacy/user-profile-data-and-privacy.component';
import { SettingsContentComponent } from './pages/home-page/shop-settings/settings-content/settings-content.component';
import { subscriptionOnlineGuard } from './pages/home-page/shop-settings/settings-subscription/settings-subscription-online.guard';
import { EditInventoryItemPageComponent } from './pages/edit-inventory-item-page/edit-inventory-item-page.component';
import { inventoryItemResolverResolver } from './utils/resolvers/inventory-item.resolver.resolver';
import { ProductsControlService } from './pages/orders/components/create-order/service/products-control.service';
import { userRoleResolver } from './services/resolvers/user-role.resolver';

export const routes: Routes = [
  {
    path: '',
    component: MainPageComponent,
  },
  {
    path: 'sign-up',
    component: SignUpPageComponent,
  },
  {
    path: 'log-in',
    component: LoginPageComponent,
    canActivate: [guestGuard],
  },
  { path: 'authenticator', component: AuthenticatorComponent },
  { path: 'forgot-password', component: ForgotPasswordPageComponent },
  { path: 'inactive-link', component: InactiveLinkPageComponent },
  {
    path: 'main',
    component: HomePageComponent,
    canActivate: [authGuard],
    resolve: { 
      userProfileReady: userRoleResolver 
    },
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      { path: 'home', component: HomeBodyComponent },
      {
        path: 'inventory',
        component: InventoryComponent,
        children: [
          { path: '', redirectTo: 'items-list', pathMatch: 'full' },
          { path: 'items-list', component: InventoryTableComponent },
          {
            path: 'upload-items-list',
            component: UploadInventoryItemsComponent,
          },
        ],
      },
      {
        path: 'inventory/edit/:itemId',
        component: EditInventoryItemPageComponent,
        resolve: {
          inventoryItem: inventoryItemResolverResolver,
        },
      },
      {
        path: 'customers',
        component: CustomersComponent,
        children: [
          { path: '', redirectTo: 'all', pathMatch: 'full' },
          { path: 'all', component: CustomersTableComponent },
        ],
      },
      {
        path: 'orders',
        component: OrdersComponent,
        children: [
          { path: '', redirectTo: 'all', pathMatch: 'full' },
          { path: 'all', component: OrdersTableComponent },
          {
            path: 'create',
            providers: [ProductsControlService],
            loadComponent: () =>
              import('./pages/orders/components/create-order/create-order.component').then(
                (m) => m.CreateOrderComponent,
              ),
          },
        ],
      },
      {
        path: 'staff',
        component: StaffTableComponent,
      },
      {
        path: 'brands', 
        loadComponent: () => import('./pages/brands/brands.component').then((m) => m.BrandsComponent),
        children: [
          { path: '', redirectTo: 'all', pathMatch: 'full' },
          { path: 'all', loadComponent: () => import('./pages/brands/components/all-brands/all-brands.component').then((m) => m.AllBrandsComponent) },
        ],
      },
      {
        path: 'profile',
        component: HeaderTabsComponent,
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' },
          { path: 'general', component: UserProfileGeneralComponent },
          { path: 'security', component: UserProfileSecurityComponent },
          { path: 'active-sessions', component: ActiveSessionsComponent },
          { path: 'notifications', component: UserProfileNotificationsComponent },
          { path: 'data-privacy', component: UserProfileDataAndPrivacyComponent },
        ],
      },
      {
        path: 'settings',
        component: HeaderTabsComponent,
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' },
          { path: 'general', component: SettingsGeneralComponent },
          { path: 'categories', component: SettingsCategoriesComponent },
          { path: 'roles', component: SettingsRolesComponent },
          { path: 'content', component: SettingsContentComponent },
          {
            path: 'subscription',
            component: SettingsSubscriptionComponent,
            canActivate: [subscriptionOnlineGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'reset-congratulations',
    component: ResetPassCongratulationsComponent,
  },
  {
    path: 'reset-congratulations',
    component: ResetPassCongratulationsComponent,
  },
  { path: 'reset-password', component: ResetPassNewComponent },
  { path: '***', component: MainPageComponent },
];
